import { DateAvailabilityResponse } from '@/models/DateAvailabilityResponse';
import { McsisConstants } from '@/models/constants/McsisConstants';
import moment from 'moment-timezone';
import 'moment/locale/fr';
import 'moment/locale/de';
import 'moment/locale/it';
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
// tslint:disable-next-line: no-var-requires
const Datepicker = require('@hokify/vuejs-datepicker').default;

interface DayInfo {
  dateStr: string;
  dayName: string;
  dayNumber: number;
  availableCount: number;
  isVacation: boolean;
  isToday: boolean;
  isDisabled: boolean;
}

/*
WeekStrip - Horizontal week band component displaying days Mon-Sat
*/
@Component({
  components: {
    Datepicker,
  },
})
export default class WeekStrip extends Vue {
  @Ref('datepicker') public datepickerRef!: any;
  @Prop({ required: true }) public availabilitySummary!: DateAvailabilityResponse[];
  @Prop({ required: true }) public selectedDate!: string;
  @Prop({ required: true }) public minDate!: string;
  @Prop({ required: true }) public maxDate!: string;

  private weekStartDate: string = '';
  public dateFormatter: string = McsisConstants.SwissDateFormat;

  /**
   * Gets the current locale for the datepicker
   */
  public get datepickerLanguage(): string {
    return this.$i18n.locale;
  }

  /**
   * Gets the selected date as a Date object for the datepicker
   */
  public get selectedDateAsDate(): Date | null {
    return this.selectedDate ? moment(this.selectedDate).toDate() : null;
  }

  /**
   * Gets the disabled dates configuration for the datepicker
   */
  public get disabledDates(): object {
    return {
      to: this.minDate ? moment(this.minDate).subtract(1, 'day').toDate() : undefined,
      from: this.maxDate ? moment(this.maxDate).add(1, 'day').toDate() : undefined,
    };
  }

  /**
   * Computed property for the current month and year display
   */
  public get currentMonthYear(): string {
    if (!this.weekStartDate) return '';
    const date = moment(this.weekStartDate).locale(this.$i18n.locale);
    // Capitalize first letter
    const monthYear = date.format('MMMM YYYY');
    return monthYear.charAt(0).toUpperCase() + monthYear.slice(1);
  }

  /**
   * Computed property for the visible days in the strip (Mon-Sat)
   */
  public get visibleDays(): DayInfo[] {
    const days: DayInfo[] = [];
    if (!this.weekStartDate) return days;

    const locale = this.$i18n.locale;
    const startMoment = moment(this.weekStartDate).locale(locale);
    const today = moment().format(McsisConstants.DefaultDateFormat);

    for (let i = 0; i < 7; i++) {
      const currentDay = startMoment.clone().add(i, 'days').locale(locale);
      
      // Skip Sunday (day() returns 0 for Sunday)
      if (currentDay.day() === 0) {
        continue;
      }

      const dateStr = currentDay.format(McsisConstants.DefaultDateFormat);

      // Find availability info for this date
      const availability = this.availabilitySummary.find(
        a => moment(a.date).format(McsisConstants.DefaultDateFormat) === dateStr
      );

      const isVacation = availability ? availability.isVacation : false;
      const availableCount = availability ? availability.availableSlotCount : 0;
      const dayName = currentDay.format('dd');

      days.push({
        dateStr,
        dayName: dayName.charAt(0).toUpperCase() + dayName.slice(1),
        dayNumber: currentDay.date(),
        availableCount,
        isVacation,
        isToday: dateStr === today,
        isDisabled: isVacation || availableCount === 0
      });
    }

    return days;
  }

  /**
   * Check if we can navigate to previous week
   */
  public get canNavigatePrev(): boolean {
    if (!this.weekStartDate || !this.minDate) return false;
    const prevWeekStart = moment(this.weekStartDate).subtract(7, 'days');
    return prevWeekStart.isSameOrAfter(moment(this.minDate), 'day');
  }

  /**
   * Check if we can navigate to next week
   */
  public get canNavigateNext(): boolean {
    if (!this.weekStartDate || !this.maxDate) return false;
    const nextWeekEnd = moment(this.weekStartDate).add(13, 'days'); // end of next week
    return nextWeekEnd.isSameOrBefore(moment(this.maxDate), 'day');
  }

  /**
   * Check if a date is the selected date
   */
  public isSelected(dateStr: string): boolean {
    return dateStr === this.selectedDate;
  }

  /**
   * Navigate to previous week
   */
  public navigatePrev(): void {
    if (!this.canNavigatePrev) return;
    this.weekStartDate = moment(this.weekStartDate)
      .subtract(7, 'days')
      .format(McsisConstants.DefaultDateFormat);
  }

  /**
   * Navigate to next week
   */
  public navigateNext(): void {
    if (!this.canNavigateNext) return;
    this.weekStartDate = moment(this.weekStartDate)
      .add(7, 'days')
      .format(McsisConstants.DefaultDateFormat);
  }

  /**
   * Select a day
   */
  public selectDay(day: DayInfo): void {
    if (day.isDisabled) return; // Cannot select disabled days (vacation or no availability)
    this.$emit('dateSelected', day.dateStr);
  }

  /**
   * Open the datepicker programmatically
   */
  public openDatepicker(): void {
    if (this.datepickerRef) {
      this.datepickerRef.showCalendar();
    }
  }

  /**
   * Handle datepicker date selection
   */
  public onDatepickerInput(date: Date): void {
    if (date) {
      const dateStr = moment(date).format(McsisConstants.DefaultDateFormat);
      this.$emit('dateSelected', dateStr);
    }
  }

  /**
   * Initialize component
   */
  protected mounted(): void {
    this.initializeWeekStart();
  }

  /**

  /**

  /**
   * Watch for selected date changes to adjust the visible week
   */
  @Watch('selectedDate')
  protected onSelectedDateChanged(): void {
    this.adjustWeekToShowSelectedDate();
  }

  /**
   * Initialize the week start date based on selected date or min date
   */
  private initializeWeekStart(): void {
    const targetDate = this.selectedDate || this.minDate || moment().format(McsisConstants.DefaultDateFormat);
    // Start from the beginning of the week containing the target date
    this.weekStartDate = moment(targetDate)
      .startOf('week')
      .format(McsisConstants.DefaultDateFormat);

    // Ensure we don't go before minDate
    if (moment(this.weekStartDate).isBefore(moment(this.minDate))) {
      this.weekStartDate = this.minDate;
    }
  }

  /**
   * Adjust the visible week to include the selected date
   */
  private adjustWeekToShowSelectedDate(): void {
    if (!this.selectedDate) return;

    const selectedMoment = moment(this.selectedDate);
    const weekStartMoment = moment(this.weekStartDate);
    const weekEndMoment = weekStartMoment.clone().add(6, 'days');

    // If selected date is not in visible range, adjust
    if (selectedMoment.isBefore(weekStartMoment) || selectedMoment.isAfter(weekEndMoment)) {
      this.weekStartDate = selectedMoment
        .startOf('week')
        .format(McsisConstants.DefaultDateFormat);

      // Ensure we don't go before minDate
      if (moment(this.weekStartDate).isBefore(moment(this.minDate))) {
        this.weekStartDate = this.minDate;
      }
    }
  }
}
